import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { GenericEdit } from 'src/app/interfaces/generic-edit';
import { HTTPService } from 'src/app/services/http.service';
import { ShopeeServices } from 'src/app/services/shopee.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { ThemeService } from 'src/app/services/theme.service';
import { ObjectUtil } from 'src/app/system-utils/utils/ObjectUtil';
import { CollectedAttr, ShopeeAttribute, multi_lang } from './shopee-attribute.types';

@Component({
  selector: 'shopee-attribute',
  templateUrl: './shopee-attribute.component.html',
  styleUrls: ['./shopee-attribute.component.scss'],
  standalone: false
})
export class ShopeeAttributeComponent implements OnInit {
  data: GenericEdit;
  @Output() saving = new EventEmitter<{ changing: boolean; keepOpen: boolean }>();
  @Input() product: Object;
  attribs: ShopeeAttribute[] = [];
  loading = false;
  isPopUp = true;

  constructor(
    private http: HTTPService, private cd: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public dataDialog, public dialogRef: MatDialogRef<any>,
    private snackBar: SnackbarService, public ThemeService: ThemeService,
    private sp: ShopeeServices) {}

  ngOnInit(): void {
    //this.data = {} as GenericEdit; // remover depois de testar tela sem popup
    this.isPopUp = this.data == null;
    this.loading = true;

    if (!this.isPopUp) {
      this.product = this.data?.parentRow?.linha || {};
      /*this.product['CODECOMPANY'] = 417;  // remover depois de testar
      this.product['CODEPRODUCT'] = 1;
      this.product['CODECONTRACT'] = 2;
      this.product['CODESHOPEE'] = 3;
      this.product['CATEGORY_ID'] = 100464;*/

      this.sp.getAttributes2(this.product).then(res =>
        this.initialize(res, this.product)
          .then(() => this.cd.detectChanges())
          .finally(() => (this.loading = false)));
    } else {
      this.initialize(this.dataDialog['shopeeresult'], this.dataDialog['product'])
        .finally(() => (this.loading = false));
    }
  }

  async initialize(shopeeresult: any, product: Object): Promise<void> {
    if (!ObjectUtil.isNotEmpty(shopeeresult)) return;
    try {
      const parsed = JSON.parse(shopeeresult['resultado']);
      const tree = parsed?.response?.list?.[0]?.attribute_tree;
      if (!ObjectUtil.isNotEmpty(tree)) return;

      this.attribs = tree;
      const saved = await this.getAttributeData(product) as Object[];
      if (ObjectUtil.isNotEmpty(saved) && saved.length) {
        const index = this.indexAttributes(this.attribs);
        for (const row of saved) {
          const node = index.get(Number(row['ATTRIBUTE_ID']));
          if (!node) continue;
          if (row['DATAVALUE'] != null) node.datavalue = this.parseValue(row['DATAVALUE'], node);
          if (row['UNITVALUE'] != null) node.unitvalue = this.parseSimple(row['UNITVALUE']);
        }
      }
    } catch {
      if (this.isPopUp) this.dialogRef.close();
    }
  }

  /** Indexa TODOS os atributos (topo + filhos) por attribute_id. */
  private indexAttributes(list: ShopeeAttribute[], map = new Map<number, ShopeeAttribute>()) {
    for (const a of list) {
      map.set(a.attribute_id, a);
      for (const v of a.attribute_value_list ?? [])
        if (v.child_attribute_list?.length) this.indexAttributes(v.child_attribute_list, map);
    }
    return map;
  }

  private parseValue(raw: string, node: ShopeeAttribute) {
    const it = node.attribute_info.input_type;
    if (Number.isNaN(Number(raw))) {
      return (it === 4 || it === 5) ? JSON.parse(raw) : raw;
    }
    return Number(raw);
  }
  private parseSimple(raw: string) {
    return Number.isNaN(Number(raw)) ? raw : Number(raw);
  }

  getAttributeName(item: ShopeeAttribute): string {
    return item.multi_lang?.find(d => d.language === 'pt-BR')?.value ?? item.name;
  }

  deleteItem(item: ShopeeAttribute): void {
    const i = this.attribs.findIndex(a => a.attribute_id === item.attribute_id);
    if (i > -1) this.attribs.splice(i, 1);
  }

  trackByAttr = (_: number, a: ShopeeAttribute) => a.attribute_id;

  okFunction() { this.salvar(this.product); }
  cancelFunction() {
    this.isPopUp ? this.dialogRef.close() : this.saving.emit({ changing: false, keepOpen: false });
  }

  private isEmpty(v: any): boolean {
    if (v == null) return true;
    if (Array.isArray(v)) return v.length === 0;
    if (typeof v === 'string') return v.trim() === '';
    return false;
  }

  /** Só valida o que está visível (collect já filtra filhos pelo valor selecionado). */
  private validate(): { ok: boolean; missing: string[] } {
    const missing: string[] = [];
    for (const { attr } of this.collect(this.attribs)) {
      if (attr.mandatory && this.isEmpty(attr.datavalue)) {
        missing.push(this.getAttributeName(attr));
      }
    }
    return { ok: missing.length === 0, missing };
  }

  /** Achata a árvore pegando só os filhos do valor selecionado. */
  private collect(
    list: ShopeeAttribute[],
    parentId: number | null = null,
    parentValueId: number | null = null,
    out: CollectedAttr[] = []
  ): CollectedAttr[] {
    for (const a of list) {
      out.push({ attr: a, parentId, parentValueId });

      const v = a.datavalue;
      const ids = Array.isArray(v) ? v : (v != null ? [v] : []);
      for (const id of ids) {
        const sel = (a.attribute_value_list ?? []).find(o => o.value_id === id);
        if (sel?.child_attribute_list?.length) {
          this.collect(sel.child_attribute_list, a.attribute_id, id, out);
        }
      }
    }
    return out;
  }

  prepareData(product: Object): Object[] {
    return this.collect(this.attribs).map(({ attr, parentId, parentValueId }) => {
      const ob: any = {
        CODECOMPANY: product['CODECOMPANY'],
        CODECONTRACT: product['CODECONTRACT'],
        CODEPRODUCT: product['CODEPRODUCT'],
        CODESHOPEE: product['CODESHOPEE'],
        ORIGINAL_ATTRIBUTE_NAME: attr.name,
        DISPLAY_ATTRIBUTE_NAME: this.getAttributeName(attr),
        IS_MANDATORY: attr.mandatory?.toString(),
        INPUT_VALIDATION_TYPE: attr.attribute_info.input_validation_type,
        FORMAT_TYPE: attr.attribute_info.format_type,
        INPUT_TYPE: attr.attribute_info.input_type,
        ATTRIBUTE_UNIT: JSON.stringify(attr.attribute_info.attribute_unit_list),
        ATTRIBUTE_VALUE_LIST: JSON.stringify(attr.attribute_value_list),
        ATTRIBUTE_ID: attr.attribute_id,
        PARENT_ATTRIBUTE_ID: parentId,    // null quando é atributo de topo
        PARENT_VALUE_ID: parentValueId,   // value_id do pai que abriu este filho
        DATAVALUE: attr.datavalue != null
          ? (typeof attr.datavalue === 'object' ? JSON.stringify(attr.datavalue) : attr.datavalue.toString())
          : null,
        UNITVALUE: attr.unitvalue != null ? attr.unitvalue.toString() : null
      };
      return ob;
    });
  }

  salvar(product: Object): void {
    const { ok, missing } = this.validate();
    if (!ok) {
      this.snackBar.showError(`Preencha os campos obrigatórios: ${missing.join(', ')}`);
      return; // não salva
    }

    this.loading = true;
    const local = {
      entityName: 'Mktpshopeeattrib',
      remove: true,
      removeCondition: `codecompany=${product['CODECOMPANY']} and codeproduct=${product['CODEPRODUCT']} and codeshopee=${product['CODESHOPEE']}`,
      data: this.prepareData(product)
    };
    this.http.post(local, 'JsonBrokerServices/save', false, true, true).subscribe({
      next: () => {
        this.snackBar.showSuccess('Atributos importados com sucesso.');
        this.isPopUp ? this.dialogRef.close() : this.saving.emit({ changing: true, keepOpen: false });
      },
      error: e => { this.loading = false; throw e; },
      complete: () => (this.loading = false)
    });
  }

  private getAttributeData(product: Object) {
    return new Promise((resolve, reject) => {
      const local = {
        entityName: 'Mktpshopeeattrib',
        showImageData: false, showImagaDataBase64: false, showPresentation: false,
        showRelationshipData: false, showRelationship: false,
        condition: `codecompany=${product['CODECOMPANY']} and codeproduct=${product['CODEPRODUCT']} and codeshopee=${product['CODESHOPEE']}`
      };
      this.http.post(local, 'JsonBrokerServices/find', false, true, true)
        .subscribe(r => resolve(r['Mktpshopeeattrib'] as Object[]), e => reject(e));
    });
  }
}